/**
 * Full-width primary/outline CTA using `Clickable` + solid theme surfaces.
 * Replaces `StackedGlassCtaButton`.
 */
import React, { useMemo } from 'react';
import { View, Text, type TextStyle, type ViewStyle } from 'react-native';
import { appTheme, useThemeColors } from '../../shared/theme';
import {
  button,
  buttonWrap,
  createSolidPrimaryCtaLabelStyle,
  createSolidPrimaryCtaSurfaceStyle,
  centeredLabelText,
} from '../../shared/theme/styles/buttons';
import { Clickable } from './Clickable';
import { ButtonLoader } from '../load-main/ButtonLoader';

export type SolidCtaVariant = 'primary' | 'outline';
export type SolidCtaSize = 'main' | 'compact';
export type SolidCtaWidthMode = 'stretch' | 'hug';

export type SolidCtaClickableProps = {
  label: string;
  onPress: () => void;
  variant?: SolidCtaVariant;
  disabled?: boolean;
  loading?: boolean;
  size?: SolidCtaSize;
  widthMode?: SolidCtaWidthMode;
  haptics?: boolean;
  accessibilityLabel?: string;
  style?: ViewStyle;
};

export function SolidCtaClickable({
  label,
  onPress,
  variant = 'primary',
  disabled = false,
  loading = false,
  size = 'main',
  widthMode = 'stretch',
  haptics = false,
  accessibilityLabel,
  style,
}: SolidCtaClickableProps) {
  const colors = useThemeColors() ?? appTheme.colors;
  const height = size === 'compact' ? 52 : 56;

  const surface_style = useMemo<ViewStyle>(() => {
    const hug_surface: ViewStyle =
      widthMode === 'hug'
        ? {
            alignSelf: 'flex-start',
            paddingHorizontal: appTheme.spacing.md,
          }
        : {};
    if (variant === 'outline') {
      return {
        ...button,
        ...hug_surface,
        height,
        backgroundColor: colors.surface,
        borderColor: colors.primaryHalf,
      };
    }
    return { ...createSolidPrimaryCtaSurfaceStyle(colors), ...hug_surface, height };
  }, [colors, height, variant, widthMode]);

  const label_style = useMemo<TextStyle>(() => {
    if (variant === 'outline') {
      return { ...centeredLabelText, fontSize: 18, fontWeight: '700', color: colors.primary };
    }
    return createSolidPrimaryCtaLabelStyle(colors);
  }, [colors, variant]);

  return (
    <View style={[widthMode === 'stretch' ? buttonWrap : { flexShrink: 0, alignSelf: 'flex-start' }, style]}>
      <Clickable
        onPress={onPress}
        disabled={disabled || loading}
        haptics={haptics}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? label}
        contentStyle={[surface_style, disabled ? { opacity: 0.55 } : null]}>
        {loading ? (
          <ButtonLoader size={20} color={variant === 'outline' ? colors.primary : colors.onPrimary ?? colors.white} />
        ) : (
          <Text style={label_style} numberOfLines={1}>
            {label}
          </Text>
        )}
      </Clickable>
    </View>
  );
}

export default SolidCtaClickable;
